import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import CssBaseline from '@material-ui/core/CssBaseline';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import List from '@material-ui/core/List';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import InboxIcon from '@material-ui/icons/MoveToInbox';
import MailIcon from '@material-ui/icons/Mail';
import { Dialog, DialogTitle, DialogContent, Tooltip } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { handleDriverPickup, openDriverPickup } from '../../reducers/uiSlice';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@material-ui/icons/Close';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import EditIcon from '@material-ui/icons/Edit';
import AssessmentIcon from '@material-ui/icons/Assessment';
import CreatePickupList from './CreatePickupList/index';
import FetchPickupLists from './FetchPickupLists';
import PickupListDetail from './PickupListDetail';
import { getPickupPartner, getPickupWarehouse } from '../../actions/driverPickupActions';

const drawerWidth = 260;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  appBar: {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  hide: {
    display: 'none',
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
  selected: {
    backgroundColor: theme.palette.primary.main,
    color: 'black',
    "&:hover": {
      backgroundColor: theme.palette.primary.light
    },
  },
  unselected: {
    "&:hover": {
      backgroundColor: theme.palette.primary.light
    },
  }
}));

export default function DriverPickup() {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const {t} = useTranslation('driverPickup');

  const beforePickupList = [
    {
      id: 0,
      text: t('create_manifest'),
      icon: <NoteAddIcon />
    },
    {
      id: 1,
      text: t('manage_list'),
      icon: <FormatListNumberedIcon />
    },
    {
      id: 2,
      text: t('list_detail'),
      icon: <EditIcon />
    }
  ]

  const afterPickupList = [
    {
      id: 3,
      text: t('review_statistics'),
      icon: <AssessmentIcon />
    }
  ]

  const openModal = useSelector(openDriverPickup);

  const [open, setOpen] = useState(true);
  const [step, setStep] = useState(0);

  const handleDrawerOpen = () => {
    setOpen(true);
  };
  const handleDrawerClose = () => {
    setOpen(false);
  };

  function handleClose() {
    dispatch(handleDriverPickup(false))
  }

  useEffect(() => {
    if(openModal) {
      dispatch(getPickupPartner());
      dispatch(getPickupWarehouse());
    }
  }, [openModal])

  return (
    <Dialog
      onClose={handleClose} 
      open={openModal}
      fullWidth={true}
      fullScreen
    >
      <DialogTitle>
        <AppBar
          position="fixed"
          className={clsx(classes.appBar, {
            [classes.appBarShift]: open,
          })}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              className={clsx(classes.menuButton, open && classes.hide)}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap>
            {t('title_driver_pickup')}
            </Typography>
            <IconButton
              onClick={handleClose}
              edge="end"
              style={{position: 'fixed', right: 20}}
            >
              <CloseIcon onClick={handleClose} style={{color: 'black'}}></CloseIcon>
            </IconButton> 
          </Toolbar>
        </AppBar>           
      </DialogTitle>
      <DialogContent dividers className={classes.boxStyle}>
        <div className={classes.root}>
          <Drawer
            className={classes.drawer}
            variant="persistent"
            anchor="left"
            open={open}
            classes={{
              paper: classes.drawerPaper,
            }}
          >
            <div className={classes.drawerHeader}>
              <IconButton onClick={handleDrawerClose}>
                {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </IconButton>
            </div>
            <Divider />
            <List>
              {beforePickupList.map((item, index) => (
                <ListItem 
                  button 
                  key={index} 
                  onClick={()=>setStep(item.id)}
                  className={step === item.id?classes.selected:classes.unselected}
                >
                  <ListItemIcon style={step === item.id?{color:'black'}:{color:'white'}}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItem>
              ))}
            </List>
            <Divider />
            {/* <List>
              {afterPickupList.map((item, index) => (
                <ListItem 
                  button 
                  key={index} 
                  onClick={()=>setStep(item.id)}
                  className={step === item.id?classes.selected:classes.unselected}
                >
                  <ListItemIcon style={step === item.id?{color:'black'}:{color:'white'}}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItem>
              ))}
            </List> */}
          </Drawer>
          <main
            className={clsx(classes.content, {
              [classes.contentShift]: open,
            })}
          >
            {
              step === 0 &&
              <CreatePickupList open={openModal} />
            }
            {
              step === 1 &&
              <FetchPickupLists open={openModal} />
            }
            {
              step === 2 &&
              <PickupListDetail open={openModal} />
            }
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}
